import React, { useState, useEffect } from 'react';
import { User, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

const StatusChangeModal = ({ show, survey, newStatus, onClose, onConfirm, isLoading }) => {
  const [receiptEmployees, setReceiptEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('12:00');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && newStatus === 'EM_PROJETACAO') {
      fetchReceiptEmployees();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7);
      setDeadlineDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [show, newStatus]);

  const fetchReceiptEmployees = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'RECEPCAO_PROJETOS', active: 'true' } });
      if (response.data?.data?.users) {
        setReceiptEmployees(response.data.data.users);
        if (response.data.data.users.length > 0) {
          setSelectedEmployee(response.data.data.users[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error);
      setError('Erro ao carregar funcionários');
    }
  };

  if (!show) return null;

  const handleConfirm = () => {
    if (newStatus === 'EM_PROJETACAO') {
      if (!selectedEmployee) {
        setError('Selecione um funcionário responsável');
        return;
      }
      const deadlineDateTime = new Date(`${deadlineDate}T${deadlineTime}:00`);
      if (isNaN(deadlineDateTime.getTime()) || deadlineDateTime <= new Date()) {
        setError('O prazo deve ser uma data/hora futura');
        return;
      }
      onConfirm({
        project_3d_created_by: parseInt(selectedEmployee),
        project_3d_deadline: deadlineDateTime.toISOString()
      });
    } else {
      onConfirm({});
    }
  };

  const getTitle = () => {
    switch (newStatus) {
      case 'VISITA_REALIZADA':
        return 'Confirmar Status';
      case 'EM_PROJETACAO':
        return 'Iniciar Projeto 3D';
      default:
        return 'Confirmar';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          {newStatus === 'VISITA_REALIZADA' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : newStatus === 'EM_PROJETACAO' ? (
            <Calendar className="w-5 h-5 text-purple-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-blue-600" />
          )}
          {getTitle()}
        </h3>

        {newStatus === 'VISITA_REALIZADA' && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Tem certeza que deseja marcar a visita como realizada?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Levantamento: <strong>{survey?.survey_code || survey?.code}</strong>
            </p>
          </>
        )}

        {newStatus === 'EM_PROJETACAO' && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Preencha os dados para iniciar o projeto 3D:
            </p>
            <p className="text-sm text-gray-500 mb-2">
              Levantamento: <strong>{survey?.survey_code || survey?.code}</strong>
            </p>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Funcionário Responsável (Recepção Projetos)
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => {
                    setSelectedEmployee(e.target.value);
                    setError('');
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Selecione...</option>
                  {receiptEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Data Limite de Entrega
                </label>
                <input
                  type="date"
                  value={deadlineDate}
                  onChange={(e) => {
                    setDeadlineDate(e.target.value);
                    setError('');
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Hora Limite
                </label>
                <input
                  type="time"
                  value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Confirmando...
              </>
            ) : (
              'Confirmar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;
